/**
 * PlazaPage - 广场浏览（统一网格·信息丰富）。
 *
 * 顶部：标题 + 副标题，右侧分类胶囊 + 排序切换；
 * 主体：响应式等比卡片网格；Load more 游标分页。
 * 公开访问（未登录可浏览）。
 */

import { useState, useEffect, useCallback, useRef, Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";
import {
  listPlaza,
  PLAZA_DOWNLOAD_COST,
  type PlazaModelSummary,
  type ModelCategory,
} from "../lib/api";
import ModelCard from "../components/ModelCard";
import { prefetchGlb } from "../lib/modelCache";

// 懒加载预览弹窗（含 three.js / R3F，约 1MB），点击卡片时才拉入。
// 预热策略：网格滚入视口时预拉（见组件内 IntersectionObserver）。
const WorkPreviewModal = lazy(() => import("../components/WorkPreviewModal"));

export default function PlazaPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<PlazaModelSummary[]>([]);
  const [category, setCategory] = useState<ModelCategory | "">("");
  const [sort, setSort] = useState<"newest" | "popular">("newest");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 游标字段按 sort 取值：newest→publishedAt，popular→likes，均配 id。
  // 后端原样回传同结构，前端不关心当前 sort 用哪一字段，按需读取即可。
  const [cursor, setCursor] = useState<{
    publishedAt: number | null;
    likes: number | null;
    id: string;
  } | null>(null);
  const [hasMore, setHasMore] = useState(true);
  // 点击卡片就地预览的模型（null 时弹窗不渲染）
  const [previewModel, setPreviewModel] = useState<PlazaModelSummary | null>(
    null,
  );
  // loadFirst 的请求序号：防止快速切换分类/排序时旧响应覆盖新结果
  const loadFirstSeq = useRef(0);
  const gridRef = useRef<HTMLDivElement>(null);

  // 网格滚入视口时预热 3D 预览 chunk（WorkPreviewModal → ModelViewer → three.js）。
  // 不预拉 GLB（流量友好），只让 JS chunk 提前就位，首点省去最重的串行等待。
  // 用滚入视口而非 requestIdleCallback：后者在 Lighthouse/PSI「加载后不交互」
  // 的审计里也会触发，1MB+ three chunk 全部计入 unused JavaScript。
  useEffect(() => {
    const el = gridRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          void import("../components/WorkPreviewModal");
        }
      },
      { rootMargin: "100% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // 分类：用 i18n key，value 仍为 ModelCategory id
  const CATEGORIES: { value: ModelCategory | ""; key: string }[] = [
    { value: "", key: "common.category.all" },
    { value: "characters", key: "common.category.characters" },
    { value: "animals", key: "common.category.animals" },
    { value: "household", key: "common.category.household" },
    { value: "toys", key: "common.category.toys" },
    { value: "art", key: "common.category.art" },
    { value: "tools", key: "common.category.tools" },
    { value: "other", key: "common.category.other" },
  ];

  const loadFirst = useCallback(async () => {
    // 请求序号守卫：快速切换分类/排序时，旧请求后返回不得覆盖新请求的结果
    const seq = ++loadFirstSeq.current;
    setLoading(true);
    setError(null);
    try {
      const result = await listPlaza({
        category: category || undefined,
        sort,
        limit: 24,
      });
      if (seq !== loadFirstSeq.current) return;
      setItems(result.items);
      setCursor(result.nextCursor);
      setHasMore(!!result.nextCursor);
    } catch (err) {
      if (seq !== loadFirstSeq.current) return;
      setError(err instanceof Error ? err.message : t("plaza.loadFailed"));
    } finally {
      if (seq === loadFirstSeq.current) setLoading(false);
    }
  }, [category, sort, t]);

  useEffect(() => {
    loadFirst();
  }, [loadFirst]);

  const loadMore = useCallback(async () => {
    // 独立的 loadingMore 守卫：loading 只反映首屏，分页期间为 false，
    // 不加守卫会导致快速双击"加载更多"把同一页追加两次。
    if (!cursor || loading || loadingMore) return;
    // 与 loadFirst 共用请求序号：切换分类/排序会令 loadFirst 自增 seq，
    // 旧分类的在途「加载更多」响应据此丢弃，不得追加进新列表（P2-9）
    const seq = loadFirstSeq.current;
    setLoadingMore(true);
    try {
      const result = await listPlaza({
        category: category || undefined,
        sort,
        // 按 sort 传回对应的游标字段（newest→publishedAt，popular→likes）。
        // 后端游标条件与 ORDER BY 严格对齐，避免丢页/乱序。
        cursorCreatedAt: cursor.publishedAt ?? undefined,
        cursorLikes: cursor.likes ?? undefined,
        cursorId: cursor.id,
        limit: 24,
      });
      if (seq !== loadFirstSeq.current) return;
      setItems((prev) => [...prev, ...result.items]);
      setCursor(result.nextCursor);
      setHasMore(!!result.nextCursor);
    } catch (err) {
      if (seq !== loadFirstSeq.current) return;
      setError(err instanceof Error ? err.message : t("plaza.loadFailed"));
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, category, sort, loading, loadingMore, t]);

  return (
    <div className="plaza-page">
      <div className="plaza-header">
        <div className="plaza-title">
          <h1>{t("plaza.title")}</h1>
          <p>{t("plaza.subtitle")}</p>
        </div>
        <div className="sort-toggle" role="group" aria-label={t("plaza.sortLabel")}>
          <button
            className={sort === "newest" ? "active" : ""}
            onClick={() => setSort("newest")}
          >
            {t("plaza.sort.newest")}
          </button>
          <button
            className={sort === "popular" ? "active" : ""}
            onClick={() => setSort("popular")}
          >
            {t("plaza.sort.popular")}
          </button>
        </div>
      </div>

      <div className="plaza-filters">
        <div className="category-tabs" role="group" aria-label={t("plaza.categoryLabel")}>
          {CATEGORIES.map((cat) => (
            <button
              key={cat.value || "all"}
              className={category === cat.value ? "active" : ""}
              onClick={() => setCategory(cat.value)}
            >
              {t(cat.key)}
            </button>
          ))}
        </div>
      </div>

      {loading && items.length === 0 && (
        <p className="plaza-loading">{t("common.loading")}</p>
      )}
      {error && <p className="error">{error}</p>}
      {!loading && items.length === 0 && !error && (
        <p className="plaza-empty">{t("plaza.empty")}</p>
      )}

      {/* sr-only h2：补齐 h1(页标题)→h3(卡片标题) 之间的层级（axe heading-order），
          视觉不占空间；卡片标题见 ModelCard */}
      <h2 className="sr-only">{t("plaza.modelsHeading")}</h2>
      <div className="plaza-grid" ref={gridRef}>
        {items.map((model) => (
          <ModelCard
            key={model.id}
            model={model}
            onOpen={(m) => setPreviewModel(m)}
            onHover={(m) =>
              prefetchGlb(`/api/plaza/${m.id}/preview`)
            }
          />
        ))}
      </div>

      {hasMore && !loading && (
        <div className="plaza-load-more">
          <button className="load-more-btn" onClick={loadMore}>
            {t("plaza.loadMore")}
          </button>
        </div>
      )}

      {/* 点击卡片就地预览 3D 模型（复用「我的作品」预览弹窗）。
          预览走公开免费的 /preview（/download 有订阅门槛+扣积分，预览复用会全员 401/403）；
          弹窗内「下载」仍走计费的 /download。
          外层 Suspense 用 spinner fallback（非 null）：chunk 加载期间用户看到
          loading 遮罩而非白屏无反馈。预热后通常已命中，fallback 极少触发。 */}
      {previewModel && (
        <Suspense fallback={<PreviewModalFallback onClose={() => setPreviewModel(null)} />}>
          <WorkPreviewModal
            glbUrl={`/api/plaza/${previewModel.id}/preview`}
            downloadUrl={`/api/plaza/${previewModel.id}/download?format=glb`}
            downloadCost={PLAZA_DOWNLOAD_COST}
            downloadName={previewModel.id}
            onClose={() => setPreviewModel(null)}
          />
        </Suspense>
      )}
    </div>
  );
}

/**
 * 预览弹窗的 Suspense fallback：chunk 加载期间显示 loading 遮罩。
 * 复用 WorkPreviewModal 的 overlay/preview-modal 样式，视觉无缝衔接。
 * 点击遮罩可关闭（与正式弹窗一致）。
 */
function PreviewModalFallback({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      className="preview-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t("preview.title")}
      onClick={onClose}
    >
      <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="model-viewer loading-screen">
          <div className="loading-spinner" />
          <p>{t("common.loadingViewer")}</p>
        </div>
      </div>
    </div>
  );
}
